import React, { useState, useCallback } from 'react';
import { 
  Paper, 
  Typography, 
  Box, 
  TextField, 
  InputAdornment, 
  Slider, 
  Switch, 
  FormControlLabel, 
  Divider, 
  Tooltip,
  Button,
  ButtonGroup,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Alert
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { TokenInfo } from '../store/dexConfigSlice';
import { AmountType } from '../types/dex';

/**
 * 交易设置组件属性
 */
interface SwapSettingsProps {
  /**
   * 代币1信息
   */
  token1: TokenInfo | null;
  /**
   * 代币2信息
   */
  token2: TokenInfo | null;
  /**
   * 交易数量
   */
  swapAmount: string;
  /**
   * 交易数量变更回调
   */
  onSwapAmountChange: (value: string) => void;
  /**
   * 卖出数量
   */
  sellAmount: string;
  /**
   * 卖出数量变更回调
   */
  onSellAmountChange: (value: string) => void;
  /**
   * 滑点值
   */
  slippage: number;
  /**
   * 滑点变更回调
   */
  onSlippageChange: (value: number) => void;
  /**
   * 是否自动卖出
   */
  autoSell: boolean;
  /**
   * 自动卖出变更回调
   */
  onAutoSellChange: (enabled: boolean) => void;
  /**
   * 是否禁用
   */
  disabled?: boolean;
  /**
   * 用户可用余额(Token1)，用于计算最大金额
   */
  token1Balance: string;
  /**
   * 用户可用余额(Token2)，用于计算最大金额
   */
  token2Balance: string;
  /**
   * 交易金额类型
   */
  amountType: string;
  /**
   * 交易金额类型变更回调
   */
  onAmountTypeChange: (type: string) => void;
  /**
   * 最小随机金额
   */
  minAmount: string;
  /**
   * 最小随机金额变更回调
   */
  onMinAmountChange: (value: string) => void;
  /**
   * 最大随机金额
   */
  maxAmount: string;
  /**
   * 最大随机金额变更回调
   */
  onMaxAmountChange: (value: string) => void;
  /**
   * 固定金额
   */
  fixedAmount: string;
  /**
   * 固定金额变更回调
   */
  onFixedAmountChange: (value: string) => void;
  /**
   * 保留金额
   */
  reserveAmount: string;
  /**
   * 保留金额变更回调
   */
  onReserveAmountChange: (value: string) => void;
  // 自动卖出相关属性
  sellAmountType: string;
  onSellAmountTypeChange: (type: string) => void;
  sellMinAmount: string;
  onSellMinAmountChange: (value: string) => void;
  sellMaxAmount: string;
  onSellMaxAmountChange: (value: string) => void;
  sellFixedAmount: string;
  onSellFixedAmountChange: (value: string) => void;
  sellReserveAmount: string;
  onSellReserveAmountChange: (value: string) => void;
}

/**
 * 交易设置组件，包含交易数量、滑点、自动卖出等设置
 */
const SwapSettings: React.FC<SwapSettingsProps> = ({
  token1,
  token2,
  swapAmount,
  onSwapAmountChange,
  sellAmount,
  onSellAmountChange,
  slippage,
  onSlippageChange,
  autoSell,
  onAutoSellChange,
  disabled = false,
  token1Balance = '0',
  token2Balance = '0',
  amountType = AmountType.ALL,
  onAmountTypeChange,
  minAmount = '',
  onMinAmountChange,
  maxAmount = '',
  onMaxAmountChange,
  fixedAmount = '',
  onFixedAmountChange,
  reserveAmount = '',
  onReserveAmountChange,
  // 自动卖出相关属性，默认值
  sellAmountType = AmountType.ALL,
  onSellAmountTypeChange,
  sellMinAmount = '',
  onSellMinAmountChange,
  sellMaxAmount = '',
  onSellMaxAmountChange,
  sellFixedAmount = '',
  onSellFixedAmountChange,
  sellReserveAmount = '',
  onSellReserveAmountChange
}) => {
  // 处理输入数字验证（只允许数字和小数点）
  const handleNumberInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, setter: (value: string) => void) => {
    const value = e.target.value;
    if (/^(\d*\.?\d*)$/.test(value) || value === '') {
      setter(value);
    }
  }, []);

  // 处理滑点滑块变化
  const handleSlippageSliderChange = useCallback((event: Event, newValue: number | number[]) => {
    onSlippageChange(newValue as number);
  }, [onSlippageChange]);

  // 格式化滑点显示
  const formatSlippage = (value: number) => {
    return `${value}%`;
  };

  // 处理自动卖出开关
  const handleAutoSellSwitch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onAutoSellChange(e.target.checked);
  }, [onAutoSellChange]);

  // 处理金额类型变更
  const handleAmountTypeChange = useCallback((e: SelectChangeEvent<string>) => {
    onAmountTypeChange(e.target.value);
  }, [onAmountTypeChange]);

  // 快捷设置交易数量
  const handleQuickSwapAmount = useCallback((type: string) => {
    onAmountTypeChange(type);
  }, [onAmountTypeChange]);

  // 快捷设置卖出数量
  const handleQuickSellAmount = useCallback((type: string) => {
    if (!token2 || !autoSell) return;
    
    // 此处简化计算，实际应用中可能需要基于预期获得的token2数量
    const estimatedToken2Amount = '100'; // 示例值，实际应从交易预估中获取
    
    switch (type) {
      case AmountType.ALL:
        // 全部卖出
        onSellAmountChange(estimatedToken2Amount);
        break;
      case AmountType.RANDOM: {
        // 随机金额
        const randomAmount = (Math.random() * parseFloat(estimatedToken2Amount)).toFixed(6);
        onSellAmountChange(randomAmount);
        break;
      }
      case AmountType.RANDOM_PERCENT: {
        // 随机百分比（10%-90%）
        const percent = 10 + Math.floor(Math.random() * 81);
        const amount = (parseFloat(estimatedToken2Amount) * percent / 100).toFixed(6);
        onSellAmountChange(amount);
        break;
      }
      case AmountType.FIXED:
        // 固定金额
        onSellAmountChange('10');
        break;
      case AmountType.RESERVE: {
        // 保留固定比例（卖出90%）
        const amount = (parseFloat(estimatedToken2Amount) * 0.9).toFixed(6);
        onSellAmountChange(amount);
        break;
      }
    }
  }, [token2, autoSell, onSellAmountChange]);

  // 处理最小随机金额变更
  const handleMinAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleNumberInputChange(e, onMinAmountChange);
  }, [onMinAmountChange, handleNumberInputChange]);

  // 处理最大随机金额变更
  const handleMaxAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleNumberInputChange(e, onMaxAmountChange);
  }, [onMaxAmountChange, handleNumberInputChange]);

  // 处理固定金额变更
  const handleFixedAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleNumberInputChange(e, onFixedAmountChange);
  }, [onFixedAmountChange, handleNumberInputChange]);

  // 处理保留金额变更
  const handleReserveAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleNumberInputChange(e, onReserveAmountChange);
  }, [onReserveAmountChange, handleNumberInputChange]);

  // 处理卖出金额类型变更
  const handleSellAmountTypeChange = useCallback((e: SelectChangeEvent<string>) => {
    onSellAmountTypeChange(e.target.value);
  }, [onSellAmountTypeChange]);

  // 处理卖出最小随机金额变更
  const handleSellMinAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleNumberInputChange(e, onSellMinAmountChange);
  }, [onSellMinAmountChange, handleNumberInputChange]);

  // 处理卖出最大随机金额变更
  const handleSellMaxAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleNumberInputChange(e, onSellMaxAmountChange);
  }, [onSellMaxAmountChange, handleNumberInputChange]);

  // 处理卖出固定金额变更
  const handleSellFixedAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleNumberInputChange(e, onSellFixedAmountChange);
  }, [onSellFixedAmountChange, handleNumberInputChange]);

  // 处理卖出保留金额变更
  const handleSellReserveAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleNumberInputChange(e, onSellReserveAmountChange);
  }, [onSellReserveAmountChange, handleNumberInputChange]);

  // 渲染交易金额输入表单
  const renderAmountInputs = () => {
    switch (amountType) {
      case AmountType.ALL:
        return (
          <Alert severity="info" sx={{ mt: 0 }}>
            将使用全部可用的{token1?.symbol || '代币'}余额
          </Alert>
        );
      case AmountType.RANDOM:
        return (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="最小金额"
              placeholder="输入最小金额"
              value={minAmount}
              onChange={handleMinAmountChange}
              disabled={disabled || !token1}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {token1?.symbol || ''}
                  </InputAdornment>
                ),
                sx: { borderRadius: 1.5 }
              }}
              size="small"
            />
            <TextField
              fullWidth
              label="最大金额"
              placeholder="输入最大金额"
              value={maxAmount}
              onChange={handleMaxAmountChange}
              disabled={disabled || !token1}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {token1?.symbol || ''}
                  </InputAdornment>
                ),
                sx: { borderRadius: 1.5 }
              }}
              size="small"
            />
          </Box>
        );
      case AmountType.RANDOM_PERCENT:
        return (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="最小百分比"
              placeholder="输入最小百分比"
              value={minAmount}
              onChange={handleMinAmountChange}
              disabled={disabled || !token1}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    %
                  </InputAdornment>
                ),
                sx: { borderRadius: 1.5 }
              }}
              size="small"
            />
            <TextField
              fullWidth
              label="最大百分比"
              placeholder="输入最大百分比"
              value={maxAmount}
              onChange={handleMaxAmountChange}
              disabled={disabled || !token1}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    %
                  </InputAdornment>
                ),
                sx: { borderRadius: 1.5 }
              }}
              size="small"
            />
          </Box>
        );
      case AmountType.FIXED:
        return (
          <TextField
            fullWidth
            label="固定金额"
            placeholder="输入固定金额"
            value={fixedAmount}
            onChange={handleFixedAmountChange}
            disabled={disabled || !token1}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {token1?.symbol || ''}
                </InputAdornment>
              ),
              sx: { borderRadius: 1.5 }
            }}
            size="small"
          />
        );
      case AmountType.RESERVE:
        return (
          <TextField
            fullWidth
            label="保留金额"
            placeholder="输入保留金额"
            value={reserveAmount}
            onChange={handleReserveAmountChange}
            disabled={disabled || !token1}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {token1?.symbol || ''}
                </InputAdornment>
              ),
              sx: { borderRadius: 1.5 }
            }}
            size="small"
          />
        );
      default:
        return null;
    }
  };

  // 渲染卖出金额输入表单
  const renderSellAmountInputs = () => {
    switch (sellAmountType) {
      case AmountType.ALL:
        return (
          <Alert severity="info" sx={{ mt: 0 }}>
            将卖出全部可用的{token2?.symbol || '代币'}余额
          </Alert>
        );
      case AmountType.RANDOM:
        return (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="最小金额"
                placeholder="输入最小金额"
                value={sellMinAmount}
                onChange={handleSellMinAmountChange}
                disabled={disabled || !token2}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {token2?.symbol || ''}
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 1.5 }
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="最大金额"
                placeholder="输入最大金额"
                value={sellMaxAmount}
                onChange={handleSellMaxAmountChange}
                disabled={disabled || !token2}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {token2?.symbol || ''}
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 1.5 }
                }}
                size="small"
              />
            </Grid>
          </Grid>
        );
      case AmountType.RANDOM_PERCENT:
        return (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="最小百分比"
                placeholder="输入最小百分比"
                value={sellMinAmount}
                onChange={handleSellMinAmountChange}
                disabled={disabled || !token2}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      %
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 1.5 }
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="最大百分比"
                placeholder="输入最大百分比"
                value={sellMaxAmount}
                onChange={handleSellMaxAmountChange}
                disabled={disabled || !token2}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      %
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 1.5 }
                }}
                size="small"
              />
            </Grid>
          </Grid>
        );
      case AmountType.FIXED:
        return (
          <TextField
            fullWidth
            label="固定金额"
            placeholder="输入固定金额"
            value={sellFixedAmount}
            onChange={handleSellFixedAmountChange}
            disabled={disabled || !token2}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {token2?.symbol || ''}
                </InputAdornment>
              ),
              sx: { borderRadius: 1.5 }
            }}
            size="small"
          />
        );
      case AmountType.RESERVE:
        return (
          <TextField
            fullWidth
            label="保留金额"
            placeholder="输入保留金额"
            value={sellReserveAmount}
            onChange={handleSellReserveAmountChange}
            disabled={disabled || !token2}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {token2?.symbol || ''}
                </InputAdornment>
              ),
              sx: { borderRadius: 1.5 }
            }}
            size="small"
          />
        );
      default:
        return null;
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        mb: 2,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider"
      }}
    >
      {/* 标题和自动卖出开关在同一行 */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2
      }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            fontSize: "1rem"
          }}
        >
          交易设置
        </Typography>

        {/* 自动卖出设置 */}
        <FormControlLabel
          control={
            <Switch 
              checked={autoSell} 
              onChange={handleAutoSellSwitch}
              disabled={disabled || !token2}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2">自动卖出</Typography>
              <Tooltip title="开启后，系统将在交易成功后按设定金额自动卖出获得的代币" arrow>
                <InfoOutlinedIcon fontSize="small" sx={{ ml: 0.5, fontSize: 16 }} />
              </Tooltip>
            </Box>
          }
        />
      </Box>

      {/* 交易意图设置部分 */}
      <Grid container spacing={2}>
        {/* 交易意图类型选择 */}
        <Grid item xs={12} md={4}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
            交易意图
            <Tooltip title="设置您想要交换的代币数量类型" arrow>
              <InfoOutlinedIcon fontSize="small" sx={{ ml: 0.5, fontSize: 16 }} />
            </Tooltip>
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={amountType}
              onChange={handleAmountTypeChange}
              disabled={disabled || !token1}
              sx={{ borderRadius: 1.5 }}
            >
              <MenuItem value={AmountType.ALL}>全部金额</MenuItem>
              <MenuItem value={AmountType.RANDOM}>随机金额</MenuItem>
              <MenuItem value={AmountType.RANDOM_PERCENT}>随机百分比</MenuItem>
              <MenuItem value={AmountType.FIXED}>固定金额</MenuItem>
              <MenuItem value={AmountType.RESERVE}>保留金额</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* 交易金额设置 */}
        <Grid item xs={12} md={8}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            金额设置
          </Typography>
          {renderAmountInputs()}
        </Grid>

        {/* 滑点设置 */}
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
            滑点设置
            <Tooltip title="设置交易滑点，较高的滑点可以提高交易成功率，但可能导致价格不利" arrow>
              <InfoOutlinedIcon fontSize="small" sx={{ ml: 0.5, fontSize: 16 }} />
            </Tooltip>
          </Typography>
          <Box sx={{ px: 1 }}>
            <Slider
              value={slippage}
              onChange={handleSlippageSliderChange}
              aria-labelledby="滑点设置"
              valueLabelDisplay="auto"
              valueLabelFormat={formatSlippage}
              step={0.1}
              min={0.1}
              max={5}
              disabled={disabled}
              sx={{ color: 'primary.main' }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">0.1%</Typography>
              <Typography variant="caption" color="text.primary" fontWeight="medium">{slippage}%</Typography>
              <Typography variant="caption" color="text.secondary">5%</Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* 自动卖出数量和快捷设置 */}
      {autoSell && (
        <>
          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            {/* 卖出意图类型选择 */}
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                卖出意图
                <Tooltip title="设置您想要卖出的代币数量类型" arrow>
                  <InfoOutlinedIcon fontSize="small" sx={{ ml: 0.5, fontSize: 16 }} />
                </Tooltip>
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={sellAmountType}
                  onChange={handleSellAmountTypeChange}
                  disabled={disabled || !token2}
                  sx={{ borderRadius: 1.5 }}
                >
                  <MenuItem value={AmountType.ALL}>全部金额</MenuItem>
                  <MenuItem value={AmountType.RANDOM}>随机金额</MenuItem>
                  <MenuItem value={AmountType.RANDOM_PERCENT}>随机百分比</MenuItem>
                  <MenuItem value={AmountType.FIXED}>固定金额</MenuItem>
                  <MenuItem value={AmountType.RESERVE}>保留金额</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* 卖出金额设置 */}
            <Grid item xs={12} md={8}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                金额设置
              </Typography>
              {renderSellAmountInputs()}
            </Grid>
          </Grid>
        </>
      )}
    </Paper>
  );
};

export default SwapSettings;
